import { useEffect, useRef, useState } from "react"

import { ContentObjectItem } from "@vertesia/common"
import { CopyIcon, LoaderCircle, Send } from "lucide-react"
import { toast } from "sonner"

import { ActionTooltipButton } from "@components/buttons/ActionTooltipButton"
import { DownloadContentButton } from "@components/buttons/DownloadContentButton"
import { TypingIndicator } from "@components/indicators/TypingIndicator"
import { SaveDocumentModal } from "@components/modals/SaveDocumentModal"

import { Message, ChatMessageRoleType } from "@models/messages"

import { useUserSession } from "@session/UserSession"

import { Button } from "@ui/button"
import { CardContent } from "@ui/card"
import { Input } from "@ui/input"
import { ScrollArea } from "@ui/scroll-area"

interface ChatPanelProps {
    document: ContentObjectItem
    interactionId: string
}
export function ChatPanel({ document, interactionId }: Readonly<ChatPanelProps>) {
    const { client } = useUserSession()

    const [messages, setMessages] = useState<Message[]>([])
    const [inputMessage, setInputMessage] = useState("")
    const [isLoading, setIsLoading] = useState(false)
    const messagesEndRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!messages.length) {
            return
        }

        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }, [messages])

    const handleSendMessage = () => {
        if (inputMessage.trim() === "") {
            return
        }

        const newMessage: Message = {
            id: messages.length,
            content: inputMessage,
            role: ChatMessageRoleType.USER,
            timestamp: new Date(),
        }

        setMessages((prevMessages) => [...prevMessages, newMessage])

        setIsLoading(true)

        client.interactions
            .execute(interactionId, {
                data: {
                    documents: [`store:${document.id}`],
                    chat: [...messages, newMessage],
                },
            })
            .then((response) => {
                const assistantResponse: Message = {
                    id: messages.length + 1,
                    content: response.result,
                    role: ChatMessageRoleType.ASSISTANT,
                    timestamp: new Date(),
                }
                setMessages((prevMessages) => [...prevMessages, assistantResponse])
            })
            .finally(() => {
                setIsLoading(false)
            })

        setInputMessage("")
    }

    const formatTimestamp = (date: Date) => {
        return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    }

    const copyToClipboard = (content: string) => {
        navigator.clipboard.writeText(content).then(() => {
            toast.success("Copied to clipboard", {
                description: "Message content copied to clipboard",
                duration: 2000,
            })
        })
    }

    const hasMessages = messages.length > 0 || isLoading

    return (
        <div className="flex flex-col h-full bg-gray-100 relative">
            <div className={`flex w-full overflow-hidden transition-all duration-700 ease-in-out ${hasMessages ? "flex-grow opacity-100 translate-y-0" : "flex-grow-0 opacity-0 -translate-y-4 pointer-events-none"}`}>
                <CardContent className="overflow-hidden p-2 h-full w-full">
                    <ScrollArea className="h-full w-full">
                        {messages.map((message) => (
                            <div key={message.id} className={`flex items-start ${message.id === 0 ? "" : "mt-4"} ${message.role === ChatMessageRoleType.USER ? "justify-end" : "justify-start"}`}>
                                <div className={`p-3 rounded-lg max-w-[70%] ${message.role === ChatMessageRoleType.USER ? "bg-blue-500 text-white" : "bg-gray-200 text-black"}`}>
                                    <span style={{ whiteSpace: "pre-line" }}>{message.content}</span>
                                    <span className="flex flex-row items-end justify-between text-xs opacity-70 block mt-1">
                                        {formatTimestamp(message.timestamp)}
                                        {message.role === "assistant" && (
                                            <div className="flex items-end gap-2">
                                                <SaveDocumentModal content={message.content} variant="ghost" size="xs" />
                                                <DownloadContentButton content={message.content} variant="ghost" size="xs" />
                                                <ActionTooltipButton icon={<CopyIcon />} variant="ghost" size="xs" tooltip="Copy to clipboard" onClick={() => copyToClipboard(message.content)} />
                                            </div>
                                        )}
                                    </span>
                                </div>
                            </div>
                        ))}
                        {isLoading && <TypingIndicator />}
                        <div ref={messagesEndRef} />
                    </ScrollArea>
                </CardContent>
            </div>
            <div className={`transition-all duration-700 ease-in-out ${hasMessages ? "relative p-2" : "absolute inset-0 flex items-center justify-center p-4"}`}>
                <div className={`w-full ${hasMessages ? "" : "max-w-2xl"}`}>
                    <div className="flex w-full space-x-4">
                        <Input value={inputMessage} onChange={(e) => setInputMessage(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSendMessage()} placeholder={isLoading ? "Waiting for answer" : "Type a message..."} disabled={isLoading} className="flex-grow bg-white border-primary-dark text-primary-dark hover:opacity-75 transition-all duration-300" />
                        <Button variant="outline" onClick={handleSendMessage} disabled={isLoading} className="bg-white transition-all duration-300">
                            {!isLoading && <Send />}
                            {isLoading && <LoaderCircle className="h-16 w-16 animate-spin motion-safe:animate-[spin_3s_linear_infinite]" />}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}
